import { useEffect, useRef } from 'react'
import { Crepe } from '@milkdown/crepe'
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react'
import { languages as ALL_CODE_LANGUAGES } from '@codemirror/language-data'
import { createEditGate } from '@/components/milkdown/editGate'
import { registerFactSuggestions } from '@/components/milkdown/jinja/factSuggestions'
import { registerJinjaNodes } from '@/components/milkdown/jinja/jinjaToken'
import { buildFormattingTopBar } from '@/components/milkdown/formattingTopBar'
import {
  addSnippetSlashItem,
  addSnippetToolbarItem,
} from '@/components/milkdown/snippet/insertSnippet'
import { SnippetPicker } from '@/components/milkdown/snippet/SnippetPicker'
import { useSnippetWiring } from '@/components/milkdown/snippet/useSnippetWiring'
import {
  MilkdownErrorBoundary,
  useToastEditorErrorOnce,
} from '@/components/MilkdownErrorBoundary'
import { useFactSuggestionSource } from '@/features/facts/hooks/useFactSuggestionSource'

export type MilkdownMode = 'edit' | 'preview'
/** The duty prompts a call renders against the full fact set. */
export type SnippetField =
  | 'conversation_script'
  | 'system_script'
  | 'system_script_inbound'
  | 'voicemail_script'
  | 'pre_call_message_instructions'
  | 'post_call_message_instructions'

export type MilkdownSnippetContext = {
  dutyId: string
  field: SnippetField
}

/**
 * Languages a fenced code block can be highlighted as, and the entire contents
 * of the block's language picker.
 *
 * Deliberately a short list rather than CodeMirror's full catalogue, for
 * usability rather than weight: this picker sits in duty scripts, where the only
 * code that shows up is a robocall's TwiML and the occasional payload or query,
 * and a hundred-entry dropdown would bury those behind a search box.
 *
 * Listing more would not cost much either way. `language-data` reaches its
 * grammars through dynamic imports, but the app bundles with `splitting: false`
 * (see build.ts), so those are inlined rather than split out — and Crepe already
 * depends on `language-data`, so every grammar ships whether or not it is named
 * here. Measured: naming these five adds 32 bytes to the entry chunk.
 */
const CODE_BLOCK_LANGUAGE_NAMES = ['XML', 'HTML', 'JSON', 'YAML', 'SQL']

const CODE_BLOCK_LANGUAGES = CODE_BLOCK_LANGUAGE_NAMES.map((name) => {
  const found = ALL_CODE_LANGUAGES.find((l) => l.name === name)
  if (!found) {
    // A rename in language-data would otherwise drop a language silently and
    // leave its blocks unhighlighted with no clue why.
    console.warn(`Milkdown: no CodeMirror language named "${name}"`)
  }
  return found
}).filter((l): l is NonNullable<typeof l> => l != null)

type MilkdownMarkdownCommonProps = {
  markdown: string
  /**
   * Revert this editor to Crepe's built-in selection (bubble) toolbar. Default
   * (unset) renders the v3 sticky top formatting toolbar. Escape hatch so any
   * surface can opt out without a rebuild if the top toolbar regresses.
   */
  disableFormattingToolbar?: boolean
  /**
   * Resolve snippet blocks for one supported duty field. Edit mode also gets
   * the toolbar button and the `/snippet` command.
   */
  snippets?: MilkdownSnippetContext
}

export type MilkdownMarkdownProps = MilkdownMarkdownCommonProps &
  (
    | {
        mode?: 'edit'
        onChange: (markdown: string) => void
      }
    | {
        mode?: 'preview'
        onChange?: never
      }
  )

/**
 * Picks the discriminated-union arm of ``MilkdownMarkdownProps`` for a
 * given archived-ness. Use with spread: ``<MilkdownMarkdownEditor {...milkdownModeProps(isArchived, onChange)} />``.
 * Keeps the type narrowing intact without duplicating the ternary at every call site.
 */
export function milkdownModeProps(
  readOnly: boolean,
  onChange: (markdown: string) => void
):
  | { mode: 'preview' }
  | { mode: 'edit'; onChange: (markdown: string) => void } {
  return readOnly
    ? { mode: 'preview' as const }
    : { mode: 'edit' as const, onChange }
}

function CrepeEditor({
  inferredMode,
  markdown,
  onChange,
  onError,
  disableFormattingToolbar = false,
  snippets,
}: {
  inferredMode: MilkdownMode
  markdown: string
  onChange?: (markdown: string) => void
  onError: (error: unknown) => void
  disableFormattingToolbar?: boolean
  snippets?: MilkdownSnippetContext
}) {
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const factSource = useFactSuggestionSource(inferredMode === 'edit')

  const {
    snippetsOn,
    snippetsInteractive,
    snippetDutyId,
    snippetSource,
    snippetTarget,
    setSnippetTarget,
    openSnippetEditor,
  } = useSnippetWiring(snippets, inferredMode)

  // The picker dialog is a React portal OUTSIDE the editor root, so the root's
  // capture listeners below never see the click that inserts. Both entry points
  // (toolbar pointerdown, typing `/snippet`) already arm the gate, but the
  // picker arms it itself rather than relying on how it was opened.
  const markEditedRef = useRef<() => void>(() => {})

  // Mirror latest markdown each render so the recreated Crepe gets fresh
  // content when Activity re-show triggers destroy+recreate.
  const initialMarkdownRef = useRef(markdown)
  initialMarkdownRef.current = markdown

  useEditor(
    (root) => {
      let crepe: Crepe
      try {
        // Edit mode: the v3 sticky top toolbar replaces Crepe's built-in
        // selection (bubble) toolbar by default — swap Toolbar off for TopBar.
        // `disableFormattingToolbar` reverts to Crepe's default bubble toolbar
        // (empty features = Toolbar on, TopBar off). BlockEdit's `/` slash menu
        // stays on (its gutter drag handle is hidden in index.css). Preview turns
        // the whole editing UI off.
        const useTopBar = inferredMode === 'edit' && !disableFormattingToolbar
        crepe = new Crepe({
          root,
          defaultValue: initialMarkdownRef.current,
          features:
            inferredMode === 'preview'
              ? {
                  [Crepe.Feature.BlockEdit]: false,
                  [Crepe.Feature.Toolbar]: false,
                  [Crepe.Feature.Placeholder]: false,
                }
              : useTopBar
                ? {
                    [Crepe.Feature.Toolbar]: false,
                    [Crepe.Feature.TopBar]: true,
                  }
                : {},
          featureConfigs: {
            // Configured in both modes so a code block highlights the same
            // whether the card is being edited or read.
            [Crepe.Feature.CodeMirror]: { languages: CODE_BLOCK_LANGUAGES },
            ...(useTopBar && {
              [Crepe.Feature.TopBar]: {
                buildTopBar: (builder) => {
                  const { insert } = buildFormattingTopBar(builder)
                  if (snippetsInteractive) {
                    addSnippetToolbarItem(insert, setSnippetTarget)
                  }
                },
              },
            }),
            ...(snippetsInteractive && {
              [Crepe.Feature.BlockEdit]: {
                buildMenu: (builder) =>
                  addSnippetSlashItem(builder, setSnippetTarget),
              },
            }),
          },
        })
      } catch (error) {
        onError(error)
        throw error
      }

      const safeGetMarkdown = () => {
        try {
          return crepe.getMarkdown()
        } catch (error) {
          // This can happen if the editor is destroyed before the view ctx is ready
          // (e.g. React strict mode mount/unmount, rapid tab/card toggles).
          console.debug(
            'Milkdown: getMarkdown failed (likely unmounted early)',
            error
          )
          return null
        }
      }

      // Only report genuine user edits. The editor fires programmatic
      // transactions at mount (cursor placement, trailing-paragraph plugin,
      // jinja normalization) whose re-serialized markdown won't match the raw
      // server baseline and would falsely arm Save/Cancel. See editGate.ts.
      const gate = createEditGate({
        getMarkdown: safeGetMarkdown,
        emit: (md) => onChangeRef.current?.(md),
      })
      markEditedRef.current = gate.markEdited

      // Coalesce flushes to one per frame: `updated` fires per transaction, so
      // a fast typist would otherwise re-serialize the whole doc on every
      // keystroke. The gate's `destroyed` guard already neutralizes a late
      // flush, and destroy cancels this and flushes synchronously below.
      let flushRaf = 0
      const scheduleFlush = () => {
        if (flushRaf) cancelAnimationFrame(flushRaf)
        flushRaf = requestAnimationFrame(() => {
          flushRaf = 0
          gate.flushIfEdited()
        })
      }

      // Jinja/template tokens become first-class INLINE-EDITABLE nodes (parse +
      // verbatim serialize + pink-pill nodeView with an editable contentDOM +
      // live-typed input rule). Registered globally so every editor instance
      // renders tokens as pills; the node's own nodeView paints the pill, so the
      // legacy decoration plugin is gone. `registerJinjaNodes` wires the verbatim
      // stringify handler (zero escapes) — there is no text-handler sanitizer;
      // the token body never reaches the default text serializer because it is
      // emitted from the node's own custom mdast handler.
      registerJinjaNodes(
        crepe.editor,
        snippetsOn
          ? {
              snippetSource,
              interactive: snippetsInteractive,
              // Read once at NodeView construction, so this must stay out of
              // the dep array below — it is fixed for a mounted editor anyway.
              dutyId: snippetDutyId,
              onEdit: snippetsInteractive ? openSnippetEditor : undefined,
            }
          : undefined
      )
      if (inferredMode === 'edit') {
        registerFactSuggestions(crepe.editor, factSource)
      }

      const editor = crepe.editor as any
      const originalCreate = editor?.create?.bind(editor)
      if (originalCreate) {
        editor.create = () =>
          originalCreate().catch((error: unknown) => {
            onError(error)
          })
      }

      const originalDestroy = editor?.destroy?.bind(editor)
      if (originalDestroy) {
        editor.destroy = async (...args: any[]) => {
          if (flushRaf) cancelAnimationFrame(flushRaf)
          flushRaf = 0
          if (inferredMode === 'edit') {
            // Flush the final edited markdown before teardown so draft
            // persistence captures it.
            try {
              gate.flushIfEdited()
            } catch (error) {
              console.error('Failed to flush markdown on destroy:', error)
            }
          }
          // Block any late debounced `updated` flush from writing post-teardown.
          gate.markDestroyed()
          // Drop the captured ctx: it belongs to the editor being torn down, so
          // holding it would pin a dead EditorView and its whole doc.
          setSnippetTarget(null)

          try {
            return await originalDestroy(...args)
          } catch (err) {
            console.debug('Milkdown: destroy failed (likely HMR race)', err)
          }
        }
      }

      if (inferredMode === 'preview') {
        crepe.setReadonly(true)
        return crepe
      }

      // Flip the gate on the first real user-input intent (capture phase, so we
      // see it before ProseMirror). `beforeinput` covers all content insertion
      // regardless of modality — typing, paste, drop, IME, dictation, autocorrect
      // — while pointerdown/keydown cover select-then-toolbar-format. Programmatic
      // transactions (mount, trailing paragraph, jinja normalization) dispatch
      // none of these, so they stay gated out. The listeners are GC'd with the
      // root's DOM subtree when the editor unmounts.
      for (const evt of ['pointerdown', 'keydown', 'beforeinput', 'drop']) {
        root.addEventListener(evt, gate.markEdited, true)
      }

      // Report edits via the `updated` (doc-level) listener: it fires on every
      // real transaction including toolbar formatting (a docChanged tx that
      // input/keyup miss), and lets us serialize via the guarded
      // safeGetMarkdown. `markdownUpdated` instead serializes inside Milkdown's
      // own debounce, which can throw uncatchably during teardown.
      crepe.on((api) => {
        api.updated(scheduleFlush)
      })

      return crepe
    },
    inferredMode === 'preview'
      ? [inferredMode, markdown]
      : [inferredMode, disableFormattingToolbar, snippetsOn]
  )

  return (
    <>
      <Milkdown />
      {snippetsInteractive && (
        <SnippetPicker
          target={snippetTarget}
          dutyId={snippetDutyId}
          onClose={() => setSnippetTarget(null)}
          onInsert={() => markEditedRef.current()}
        />
      )}
    </>
  )
}

export function MilkdownMarkdownEditor(props: MilkdownMarkdownProps) {
  const onChange = 'onChange' in props ? props.onChange : undefined
  const inferredMode: MilkdownMode =
    props.mode ?? (onChange ? 'edit' : 'preview')
  const effectiveOnChange = inferredMode === 'edit' ? onChange : undefined

  if (inferredMode === 'edit' && typeof onChange !== 'function') {
    throw new Error('MilkdownMarkdown: `onChange` is required in edit mode.')
  }

  const toastEditorError = useToastEditorErrorOnce()

  return (
    <MilkdownProvider>
      <MilkdownErrorBoundary onError={toastEditorError}>
        <CrepeEditor
          inferredMode={inferredMode}
          markdown={props.markdown}
          onChange={effectiveOnChange}
          onError={toastEditorError}
          disableFormattingToolbar={props.disableFormattingToolbar}
          snippets={props.snippets}
        />
      </MilkdownErrorBoundary>
    </MilkdownProvider>
  )
}
